import Dexie from 'dexie';
import {
    Answer,
    Publish,
    QuestionerAnswersDBType,
    QuestionerPublishDBType,
    UserAnswersDBType,
    ReportDetail,
    FileStorageDBType,
} from 'interface';
import { publishDataStatus } from './storage';

class Database extends Dexie {
    questionerAnswers: Dexie.Table<QuestionerAnswersDBType, number>;
    questionerPublishes: Dexie.Table<QuestionerPublishDBType, number>;
    userAnswers: Dexie.Table<UserAnswersDBType, number>;
    fileStorage: Dexie.Table<FileStorageDBType, string>;
    constructor(databaseName) {
        super(databaseName);
        this.version(2).stores({
            questionerAnswers: '++id,publish,offline',
            questionerPublishes: '++id,form',
            userAnswers: '++id,form,workspace',
            fileStorage: '++name',
        });
        this.questionerAnswers = this.table('questionerAnswers');
        this.questionerPublishes = this.table('questionerPublishes');
        this.userAnswers = this.table('userAnswers');
        this.fileStorage = this.table('fileStorage');
    }
}

const db = new Database('ucheckApp');

export function DBDestroy() {
    db.questionerAnswers.clear();
    db.questionerPublishes.clear();
    db.userAnswers.clear();
    db.fileStorage.clear();
}

export function setPublishDB(list: Publish[]) {
    return new Promise<boolean>((resolve) => {
        db.questionerPublishes.clear().then(() => {
            db.questionerPublishes
                .bulkAdd(
                    list.map((item) => ({
                        id: item.id,
                        form: item.form.id,
                        data: JSON.stringify(item),
                    })),
                )
                .then(() => {
                    publishDataStatus.stored();
                    resolve(true);
                })
                .catch((err) => {
                    resolve(false);
                });
        });
    });
}

export function getPublishesDB() {
    return new Promise<Publish[]>((resolve) => {
        db.questionerPublishes.toArray().then((res) => {
            resolve(res.map((item) => JSON.parse(item.data)));
        });
    });
}

export function setPublishAnswers(answers: Answer[], offline = 0) {
    return new Promise<boolean>((resolve) => {
        db.questionerAnswers
            .bulkPut(
                answers.map((item) => ({
                    id: item.id,
                    publish: item.publish,
                    offline,
                    data: JSON.stringify(item),
                })),
            )
            .then((res) => {
                resolve(true);
            })
            .catch((err) => {
                console.error(err);
            });
    });
}

export function getPublishAnswersCount(publishId: number) {
    return new Promise<number>((resolve) => {
        db.questionerAnswers
            .where('publish')
            .equals(publishId)
            .count()
            .then((res) => {
                resolve(res);
            });
    });
}

export function getPublishesOfflineAnswersCount() {
    return new Promise<number>((resolve) => {
        db.questionerAnswers
            .where('offline')
            .equals(1)
            .count()
            .then((res) => {
                resolve(res);
            });
    });
}

export function getDBAnswer(id: number) {
    return new Promise<Answer | null>((resolve) => {
        db.questionerAnswers
            .where('id')
            .equals(id)
            .first()
            .then((res) => {
                if (res) {
                    try {
                        resolve(JSON.parse(res.data));
                    } catch (err) {
                        resolve(null);
                    }
                } else resolve(null);
            });
    });
}

export function getDBPublish(id: number) {
    return new Promise<Publish | null>((resolve) => {
        db.questionerPublishes
            .where('id')
            .equals(id)
            .first()
            .then((res) => {
                if (res) {
                    try {
                        resolve(JSON.parse(res.data));
                    } catch (err) {
                        resolve(null);
                    }
                } else resolve(null);
            });
    });
}

export function getDBPublishAnswers(publish: number) {
    return new Promise<Answer[]>((resolve) => {
        db.questionerAnswers
            .where('publish')
            .equals(publish)
            .toArray()
            .then((res) => {
                resolve(res.map((item) => JSON.parse(item.data)));
            });
    });
}

export function getDBPublishesOfflineAnswers() {
    return new Promise<Answer[]>((resolve) => {
        db.questionerAnswers
            .where('offline')
            .equals(1)
            .toArray()
            .then((res) => {
                resolve(res.map((i) => JSON.parse(i.data)));
            });
    });
}

export function deleteDBQuestionerAnswer(id: number) {
    db.questionerAnswers.where('id').equals(id).delete();
}

// =======================================================================
// users answer ==========================================================
// =======================================================================
export function getDBUserAnswers(form: number) {
    return new Promise<ReportDetail[]>((resolve) => {
        db.userAnswers
            .where('form')
            .equals(form)
            .reverse()
            .toArray()
            .then((res) => {
                resolve(res.map((item) => JSON.parse(item.data)));
            });
    });
}

export function getDBUserAnswer(answer: number) {
    return new Promise<ReportDetail | null>((resolve) => {
        db.userAnswers
            .where('id')
            .equals(answer)
            .toArray()
            .then((res) => {
                if (res.length) {
                    try {
                        resolve(JSON.parse(res[0].data));
                    } catch (err) {
                        resolve(null);
                    }
                } else resolve(null);
            });
    });
}

export function setDBUserAnswers(answers: ReportDetail[], form: number, workspace: number) {
    return new Promise<boolean>((resolve) => {
        db.userAnswers.bulkPut(
            answers.map((item) => ({
                id: item.id,
                form: form,
                workspace: workspace,
                data: JSON.stringify(item),
            })),
        );
    });
}

export function getFormAnswersCount(formId: number) {
    return new Promise<number>((resolve) => {
        db.userAnswers
            .where('form')
            .equals(formId)
            .count()
            .then((res) => {
                resolve(res);
            });
    });
}

// =======================================================================
// file storage ==========================================================
// =======================================================================
export function setDBFileMime(name: string, mime: string) {
    return new Promise<boolean>((resolve) => {
        db.fileStorage.put({ name, mime }).then((res) => {
            resolve(true);
        });
    });
}

export function getDBFileMime(name: string) {
    return new Promise<string | null>((resolve) => {
        db.fileStorage
            .where('name')
            .equals(name)
            .toArray()
            .then((res) => {
                if (res.length) {
                    resolve(res[0].mime);
                } else resolve(null);
            });
    });
}

export function delDBFileMime(name: string) {
    return new Promise<boolean>((resolve) => {
        db.fileStorage
            .where('name')
            .equals(name)
            .delete()
            .finally(() => {
                resolve(true);
            });
    });
}
